// Частые блоки: ряд заготовок, которые заводят почти в каждой поездке.
//
// Живёт свёрнутым разделом под кнопками библиотеки — рядом с «+ Блок», но ниже
// него: это ускорение привычного пути, а не замена ему. Раскрывается само
// только в пустой библиотеке, где человеку ещё нечего искать.
//
// Нажатие сразу кладёт блок в библиотеку, без модала: заготовок обычно нужно
// несколько подряд, и окно после каждой мешало бы. Чтобы нажатие не выглядело
// провалившимся, чипс на пару секунд отвечает галочкой, а невидимая строка
// сообщает о том же экранному диктору.
//
// «Из источника» здесь больше не живёт — вход в распознавание переехал в
// KindPicker.tsx, на шаг выбора типа блока в «+ Блок» (см. комментарий там).
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useT } from '../i18n';
import { KindIcon } from '../ui/library/KindIcon';
import { BLOCK_PRESETS, blockCountLabel, blockLabel, type BlockSpec } from './catalog';
import styles from './BlockPresets.module.css';

/** Сколько держится отметка «добавлено» на чипсе. */
const ADDED_MS = 1800;

export interface BlockPresetsProps {
  /** Раскрыть сразу — библиотека пуста, и подсказать уместно. */
  defaultOpen?: boolean;
  onAdd(spec: BlockSpec): void;
}

export function BlockPresets({ defaultOpen = false, onAdd }: BlockPresetsProps): ReactElement {
  const t = useT();
  // Начальное состояние запоминается один раз: первый же добавленный блок делает
  // библиотеку непустой, и «раскрыть, пока пусто» захлопнуло бы раздел прямо
  // под рукой. Дальше разделом распоряжается человек.
  const [open] = useState(defaultOpen);
  const [added, setAdded] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  function handleAdd(spec: BlockSpec): void {
    onAdd(spec);
    setAdded(spec.id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAdded(null), ADDED_MS);
  }

  const addedSpec = BLOCK_PRESETS.find((s) => s.id === added) ?? null;

  return (
    <details className={styles.wrap} open={open}>
      <summary className={styles.summary}>{t('tpl.blocks')}</summary>
      <p className={styles.hint}>{t('tpl.blocks.hint')}</p>
      <div className={styles.chips}>
        {BLOCK_PRESETS.map((spec) => {
          const count = blockCountLabel(spec, t);
          return (
            <button
              key={spec.id}
              type="button"
              className={styles.chip}
              data-added={added === spec.id ? 'true' : undefined}
              onClick={() => handleAdd(spec)}
            >
              {added === spec.id ? (
                <svg
                  className={styles.check}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <polyline points="4 13 9 18 20 6" />
                </svg>
              ) : (
                <KindIcon kind={spec.kind} size={12} />
              )}
              {blockLabel(spec, t)}
              {count !== '' && <span className={styles.count}>{count}</span>}
            </button>
          );
        })}
      </div>
      <p className="visually-hidden" role="status">
        {addedSpec ? t('tpl.added', { name: blockLabel(addedSpec, t) }) : ''}
      </p>
    </details>
  );
}
